package mapsearch

import apiclient.geoobjects.ObjectTags
import apiclient.geoobjects.ObjectType
import apiclient.geoobjects.SiteRestriction
import apiclient.groups.Group
import apiclient.groups.KeyWordLayerDefinition
import apiclient.groups.LayerStatus
import apiclient.groups.MapLayerUserSettings
import apiclient.mapsearch.SearchSpec
import apiclient.tags.tag
import auth.WorkspacesStore
import dev.fritz2.core.RootStore
import koin.withKoin
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.combine
import map.MapLayerUserSettingsStore
import mapsearch.buildings.ActiveFloorsStore
import mapsearch.globalsearch.GlobalSearchSpecStore
import mapsearch.globalsearch.globalSearchObjectTypes
import mu.KotlinLogging

data class KwLayerWithSettings(val definition: KeyWordLayerDefinition, val settings: MapLayerUserSettings) {
    val id = definition.layerId

    override fun toString(): String {
        return "$id - ${settings.layerStatus} - $definition"
    }
}

private val log = KotlinLogging.logger("SearchSpecStore")

val mapSearchObjectTypes = listOf(
    ObjectType.POI,
    ObjectType.Task,
    ObjectType.Event,
    ObjectType.ObjectMarker,
)

class SearchSpecStore : RootStore<SearchSpec?>(null, Job()) {

    fun syncSearchSpecChanges() {
        processMapLayerChanges()
        processGlobalSearchChange()
        processFloorChanges()
    }

    private fun processMapLayerChanges() {
        withKoin {
            val mapLayerUserSettingsStore = get<MapLayerUserSettingsStore>()
            val workspacesStore = get<WorkspacesStore>()
            val searchSwitch = get<SearchSwitch>()

            combine(
                workspacesStore.data,
                mapLayerUserSettingsStore.data,
                searchSwitch.data,
            ) { ws, mus, sw -> Triple(ws, mus, sw) } handledBy { (groups, layerSettings, searchLead) ->
                try {
                    if (searchLead == SearchLead.Map && groups.isNotEmpty()) {
                        newSettings(layerSettings, groups)
                    }
                } catch (e: Exception) {
                    log.error(e) { "error processing layer settings for search spec" }
                }
            }
        }
    }

    private fun processGlobalSearchChange() {
        withKoin {
            val workspacesStore = get<WorkspacesStore>()
            val searchSwitch = get<SearchSwitch>()
            val globalSearchSpecStore = get<GlobalSearchSpecStore>()

            combine(
                workspacesStore.data,
                globalSearchSpecStore.data,
                searchSwitch.data,
            ) { ws, gsc, sw -> Triple(ws, gsc, sw) } handledBy { (groups, globalSearchSpec, searchLead) ->
                try {
                    if (searchLead == SearchLead.Global && groups.isNotEmpty()) {
                        applyGlobalSearchContext(groups, globalSearchSpec)
                    }
                } catch (e: Exception) {
                    log.error(e) { "error processing layer settings for search spec" }
                }
            }
        }
    }

    private fun applyGlobalSearchContext(
        groups: List<Group>,
        globalSearchSpec: SearchSpec,
    ) {
        val ownerIds = groups.map { g -> g.groupId } + groups.flatMap { it.sharedLayerDefinitions?.map { it.layerId }.orEmpty() }

        current?.let { oldSpec ->
            update(
                oldSpec.copy(
                    ownerIds = ownerIds,
                    query = globalSearchSpec.query,
                    objectTypes = globalSearchSpec.objectTypes,
                    tags = globalSearchSpec.tags,
                    orTags = globalSearchSpec.orTags,
                    excludeTags = globalSearchSpec.excludeTags,
                    near = globalSearchSpec.near,
                ),
            )
        } ?: run {
            try {
                update(
                    SearchSpec(
                        ownerIds = ownerIds,
                        query = globalSearchSpec.query,
                        objectTypes = globalSearchSpec.objectTypes ?: globalSearchObjectTypes,
                        tags = globalSearchSpec.tags,
                        orTags = globalSearchSpec.orTags,
                        excludeTags = globalSearchSpec.excludeTags,
                        near = globalSearchSpec.near,
                    ),
                )
            } catch (e: Exception) {
                log.error(e) { "Error handling groups update $groups" }
            }
        }
    }

    private fun newSettings(
        layerSettings: List<MapLayerUserSettings>,
        groups: List<Group>
    ) {
        val ownerIds = groups.map { g -> g.groupId } + groups.flatMap { it.sharedLayerDefinitions?.map { it.layerId }.orEmpty() }

        (current ?: SearchSpec(
            ownerIds = ownerIds,
            objectTypes = mapSearchObjectTypes,
        )
            ).let { oldSpec ->
                val kwLayers = layerSettings.mapNotNull { settings ->
                    groups.map { it.keywordLayerDefinitions }.firstNotNullOfOrNull {
                        it?.firstOrNull { keyWordLayerDefinition ->
                            keyWordLayerDefinition.layerId == settings.layerId
                        }
                    }?.let { keyWordLayerDefinition ->
                        KwLayerWithSettings(keyWordLayerDefinition, settings)
                    }
                }

//                val otherLayers = layerSettings.mapNotNull { settings ->
//
//                    DefaultLayers.entries.firstOrNull { defaultLayer ->
//                        defaultLayer.mapLayerMetaData.id == settings.layerId
//                    }?.groups.map { it.layerDefaultSettings }.firstNotNullOfOrNull {
//                        it?.firstOrNull { layerMetaData ->
//                            layerMetaData.id == settings.layerId && DefaultLayers.NewlyTrackedObjectMarkers.mapLayerMetaData.id
//                        }
//                    }?.let { defaultLayerDefinition ->
//                        KwLayerWithSettings(defaultLayerDefinition, settings)
//                    }
//                }
//            log.info { kwLayers }

                val excludeKeywords =
                    kwLayers.filter { it.settings.layerStatus == LayerStatus.OFF }
                        .flatMap { it.definition.keywords }
                        .map { ObjectTags.Keyword.tag(it) }
                val excludeFields =
                    kwLayers.filter { it.settings.layerStatus == LayerStatus.OFF }
                        .flatMap { it.definition.fieldValues ?: emptyList() }
                        .map { ObjectTags.CustomField.tag(it) }

                update(
                    oldSpec.copy(
                        ownerIds = ownerIds,
                        query = null,
                        objectTypes = mapSearchObjectTypes, //oldSpec.objectTypes,
                        tags = null, //oldSpec.tags,
                        orTags = null, //oldSpec.orTags,
                        excludeTags = excludeFields + excludeKeywords,
                        near = null,
                    ),
                )
            }
    }

    private fun processFloorChanges() {
        withKoin {
            val activeFloorsStore = get<ActiveFloorsStore>()

            activeFloorsStore.data handledBy { floorIds ->
                try {
//                    log.info { "New SiteRestriction ${floorIds.toString()}" }
                    current?.let { oldSpec ->
                        val newSiteRestriction = if (!floorIds.isNullOrEmpty()) {
                            oldSpec.siteRestriction?.copy(floorIds = floorIds)
                                ?: SiteRestriction(floorIds = floorIds)
                        } else null
                        update(oldSpec.copy(siteRestriction = newSiteRestriction))
                    }
                } catch (e: Exception) {
                    log.error(e) { "error processing site restriction for search spec" }
                }
            }
        }
    }

    init {
        data handledBy { spec ->
            console.log("SearchSpec log:", spec.toString())
        }
    }
}
